import { Send } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { renderTemplate, templateSlotLabels } from '@aeris/communication'
import { Button, Field, Input, Select } from '@aeris/ui'

import type { TemplateSummaryType } from '../lib/api.ts'

import { WhatsAppPreview } from './whatsapp-preview.tsx'

/**
 * O que o campo de texto vira fora da janela de 24h — F050, extraído na F085.
 *
 * Ele SUBSTITUI o campo livre em vez de aparecer ao lado dele, porque fora da
 * janela não há texto livre a oferecer: o gateway recusaria, e um campo
 * habilitado que sempre falha é pior do que campo nenhum.
 *
 * Saiu de `atendimento.tsx` na F085 sem mudar de comportamento — é a mesma
 * prévia (F062), a mesma contagem de variáveis, a mesma filtragem de modelos.
 */

/** Os índices distintos `{{1}}`, `{{2}}`… referenciados em HEADER/BODY — a mesma contagem que `fillTemplateVariables` exige no servidor. */
export const templateVariableCount = (
  template: TemplateSummaryType
): number => {
  const indexes = new Set<number>()
  for (const component of template.structure.components) {
    if (
      (component.type === 'HEADER' || component.type === 'BODY') &&
      component.text !== null
    ) {
      for (const match of component.text.matchAll(/\{\{(\d+)\}\}/g)) {
        const digits = match[1]
        if (digits !== undefined) indexes.add(Number(digits))
      }
    }
  }
  return indexes.size
}

export type TemplateComposerProps = {
  templates: readonly TemplateSummaryType[]
  now: Date
  sending: boolean
  error: string | null
  onSend: (input: { templateId: string; values: string[] }) => void
  /**
   * Os valores com que as variáveis abrem ao escolher o modelo (F135): a
   * cobrança já sabe o nome e o valor, e digitar de novo é onde o erro entra.
   */
  prefill?: ((template: TemplateSummaryType) => readonly string[]) | undefined
}

export const TemplateComposer = ({
  templates,
  now,
  sending,
  error,
  onSend,
  prefill,
}: TemplateComposerProps): ReactNode => {
  const [selectedId, setSelectedId] = useState('')
  const [values, setValues] = useState<string[]>([])

  const selected = templates.find((template) => template.id === selectedId)
  const variableCount =
    selected === undefined ? 0 : templateVariableCount(selected)

  const choose = (id: string): void => {
    setSelectedId(id)
    const template = templates.find((item) => item.id === id)
    if (template === undefined) {
      setValues([])
      return
    }
    const count = templateVariableCount(template)
    const initial = prefill?.(template) ?? []
    setValues(Array.from({ length: count }, (_, index) => initial[index] ?? ''))
  }

  return (
    <div className="flex max-h-96 flex-col gap-2 overflow-y-auto border-t border-(--border-default) bg-(--surface) px-3 py-2">
      <Field
        label="Modelo aprovado"
        hint={
          templates.length === 0
            ? 'Nenhum modelo aprovado para esta conta ainda — cadastre um em Atendimento → Modelos.'
            : undefined
        }
      >
        {(field) => (
          <Select
            {...field}
            value={selectedId}
            onChange={(event) => {
              choose(event.target.value)
            }}
            disabled={templates.length === 0}
          >
            <option value="">Escolha um modelo</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.metaTemplateName} ({template.language})
              </option>
            ))}
          </Select>
        )}
      </Field>

      {selected !== undefined && variableCount > 0 ? (
        <div className="flex flex-col gap-2">
          {templateSlotLabels({
            structure: selected.structure,
            variables: selected.variables,
          }).map((slot, index) => (
            <Field
              // A posição É a própria identidade da variável ({{1}}, {{2}}…).
              key={slot.index}
              label={slot.name ?? `Variável {{${String(slot.index)}}}`}
              {...(slot.example === null
                ? {}
                : { hint: `Exemplo: ${slot.example}` })}
            >
              {(field) => (
                <Input
                  {...field}
                  value={values[index] ?? ''}
                  onChange={(event) => {
                    const next = [...values]
                    next[index] = event.target.value
                    setValues(next)
                  }}
                  placeholder={slot.example ?? ''}
                />
              )}
            </Field>
          ))}
        </div>
      ) : null}

      {/*
        F062 — a prévia ANTES de enviar. O atendente escolhia um modelo por
        `metaTemplateName`, preenchia campos rotulados "Variável {{1}}" e
        mandava sem nunca ver o texto — uma mensagem que a Meta COBRA.
      */}
      {selected === undefined ? null : (
        <WhatsAppPreview
          rendered={renderTemplate({
            structure: selected.structure,
            values,
          })}
          at={now}
          status="sent"
          slotLabels={templateSlotLabels({
            structure: selected.structure,
            variables: selected.variables,
          })}
        />
      )}

      <Button
        type="button"
        variant="primary"
        className="self-start"
        disabled={selected === undefined || sending}
        loading={sending}
        leadingIcon={<Send aria-hidden="true" className="h-3.5 w-3.5" />}
        onClick={() => {
          if (selected === undefined) return
          onSend({
            templateId: selected.id,
            values: values.slice(0, variableCount),
          })
        }}
      >
        Enviar modelo
      </Button>

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}
    </div>
  )
}
